/**
 * ScoringRubric.jsx — Wave 6 · Batch 3 · Cluster B Turn 1 (anchor).
 *
 * Chrome-only sweep of the funding-readiness rubric editor. Business
 * logic — the 4 bucket taxonomies, the 4 tier keys, the 0-100 clamp
 * invariant, the `dirty` memo, the `scenarioResults` memo, and the
 * PATCH /admin/scoring-rubric + POST /admin/scoring-rubric/restore-defaults
 * pipeline — is preserved BYTE-EXACT.
 *
 * Cluster B Turn 1 changes:
 *   - Legacy CSS variables and hex literals migrated to the DS token set.
 *   - `TIER_COLOR` and `PATH_COLOR` maps: keys and semantic mapping
 *     preserved byte-exact; values swapped to semantic token names
 *     (success, accent, warning, danger, info). Consumers build the
 *     CSS variable references at render-time.
 *   - Bespoke ConfirmModal component deleted; both call-sites migrated
 *     to the shared DS ConfirmDialog primitive (Save = default variant,
 *     Restore = destructive variant, since Restore overwrites the config
 *     document).
 *   - Local Field primitive prototype (uppercase-label + input pair)
 *     wraps every NumberField. Kept LOCAL for Turn 1 — will lift to
 *     _primitives.jsx in Turn 2 only if it drops verbatim into
 *     CreditCatalog.
 *   - Top actions: raw button chrome swapped to GhostButton and
 *     AccentButton from _primitives.
 *   - Every Section shell now emits a testid for capture selectors.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import ConfirmDialog from "@/design-system/components/ConfirmDialog";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import { api, formatApiError } from "@/api/client";
import { calculate } from "@/lib/readinessEngineClient";
import { SCENARIOS } from "@/lib/scoringScenarios";
import { Section, GhostButton, AccentButton } from "@/admin/_primitives";

const CREDIT_BUCKETS = [
  { key: "750+", label: "750+" },
  { key: "720-749", label: "720–749" },
  { key: "680-719", label: "680–719" },
  { key: "640-679", label: "640–679" },
  { key: "600-639", label: "600–639" },
  { key: "<600", label: "<600" },
  { key: "null", label: "Unknown" },
];

const PAYMENT_BUCKETS = [
  { key: "none_48_months", label: "No lates 48mo" },
  { key: "older_than_48_months", label: "Older 48mo" },
  { key: "one_or_two_recent", label: "1–2 recent" },
  { key: "multiple_recent", label: "Multiple recent" },
  { key: "null", label: "Unknown" },
];

const REV_BUCKETS = [
  { key: ">20000", label: ">$20k" },
  { key: "10000-20000", label: "$10k–$20k" },
  { key: "5000-9999", label: "$5k–$10k" },
  { key: "0-4999", label: "$0–$5k" },
  { key: "null", label: "None" },
];

const TIB_BUCKETS = [
  { key: ">=24", label: "≥24mo" },
  { key: ">=12", label: "≥12mo" },
  { key: ">=6", label: "≥6mo" },
  { key: "<6", label: "<6mo" },
];

const TIER_KEYS = [
  { key: "funding_ready", label: "Funding Ready" },
  { key: "almost_ready", label: "Almost Ready" },
  { key: "improvement_recommended", label: "Improvement Recommended" },
  { key: "repair_required", label: "Repair Required" },
];

/**
 * Semantic tier→color mapping. Keys unchanged from the pre-sweep file;
 * values swapped to semantic DS token names. Consumers build the CSS
 * variable references at render-time via `var(--ds-color-${x})` /
 * `var(--ds-color-${x}-soft)`.
 *
 * Semantic pairing:
 *   funding_ready            -> success
 *   almost_ready             -> accent
 *   improvement_recommended  -> warning
 *   repair_required          -> danger
 */
const TIER_COLOR = {
  funding_ready: "success",
  almost_ready: "accent",
  improvement_recommended: "warning",
  repair_required: "danger",
};

/**
 * Semantic funding-path→color mapping. Same treatment as TIER_COLOR.
 *
 * Semantic pairing:
 *   startup  -> accent
 *   business -> info
 */
const PATH_COLOR = {
  startup: "accent",
  business: "info",
};

function clamp(val, min, max) {
  const n = Number(val);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

export default function AdminScoringRubric() {
  const { has } = useAdminPermissions();
  const canEdit = has("edit_scoring_rubric");
  const [savedRubric, setSavedRubric] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRestore, setShowRestore] = useState(false);
  const [saving, setSaving] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/scoring-rubric");
      setSavedRubric(data);
      setDraft(JSON.parse(JSON.stringify(data)));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { reload(); }, []);

  const dirty = useMemo(() => savedRubric && draft && JSON.stringify(savedRubric) !== JSON.stringify(draft), [savedRubric, draft]);

  const scenarioResults = useMemo(() => {
    if (!savedRubric || !draft) return [];
    return SCENARIOS.map((s) => ({
      scenario: s,
      saved: calculate(s.profile, savedRubric),
      draft: calculate(s.profile, draft),
    }));
  }, [savedRubric, draft]);

  function updateDraft(updater) {
    setDraft((d) => {
      const copy = JSON.parse(JSON.stringify(d));
      updater(copy);
      return copy;
    });
  }

  function setBucket(track, group, bucket, value) {
    updateDraft((d) => { d[track][group][bucket] = clamp(value, 0, 100); });
  }
  function setBfFlat(bucket, value) {
    updateDraft((d) => { d.track_b.business_foundation_pts[bucket] = clamp(value, 0, 100); });
  }
  function setBfTib(bucket, value) {
    updateDraft((d) => { d.track_b.business_foundation_pts.time_in_business[bucket] = clamp(value, 0, 100); });
  }
  function setPrep(bucket, value) {
    updateDraft((d) => { d.track_a.funding_preparation_pts[bucket] = clamp(value, 0, 100); });
  }
  function setTier(bucket, value) {
    updateDraft((d) => { d.tier_thresholds[bucket] = clamp(value, 0, 100); });
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = { track_a: draft.track_a, track_b: draft.track_b, tier_thresholds: draft.tier_thresholds };
      const { data } = await api.patch("/admin/scoring-rubric", payload);
      toast.success(`Rubric updated. ${data.affected_users_pending_recalc ?? 0} users will recompute on next assessment.`);
      setShowConfirm(false);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function handleRestore() {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/scoring-rubric/restore-defaults");
      toast.success(`Rubric restored to defaults. ${data.affected_users_pending_recalc ?? 0} users will recompute on next assessment.`);
      setShowRestore(false);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  if (loading || !draft || !savedRubric) {
    return (
      <AdminLayout title="Scoring rubric">
        <div className="text-sm" style={{ color: "var(--ds-color-ink-muted)" }}>Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Scoring rubric"
      subtitle="Edit point allocations and tier floors. Live what-if shows impact on five scenarios."
      actions={
        canEdit && (
          <div className="flex items-center gap-2">
            <GhostButton data-testid="rubric-restore" onClick={() => setShowRestore(true)}>
              <RotateCcw className="w-3 h-3" /> Restore defaults
            </GhostButton>
            <AccentButton
              data-testid="rubric-save"
              disabled={!dirty || saving}
              onClick={() => setShowConfirm(true)}
            >
              <Save className="w-3.5 h-3.5" /> Save changes
            </AccentButton>
          </div>
        )
      }
    >
      <AdminPageHelp
        title="Scoring Rubric"
        purpose="Sets the exact point allocation for each factor of the Funding Readiness Score and the tier floors that map a raw score to Funding Ready / Almost Ready / Improvement Recommended / Repair Required."
        configures="Point values for every credit / payment / revenue / TIB / business-foundation bucket across Track A (startup) and Track B (established business), plus the 4 tier thresholds."
        impact="Every FUTURE readiness assessment uses the new rubric. Historic assessments stay pinned to the rubric they were computed against. The live what-if panel on the right shows exactly what the change does to 5 representative user profiles before you save."
        safety="Yes. Use Revert to drop unsaved edits, or Restore defaults to reset to platform-supplied values (logged as a warning-severity audit event)."
      />
      {!canEdit && (
        <div
          className="mb-4 rounded-[var(--ds-radius-input)] p-3 text-xs"
          style={{
            background: "var(--ds-color-warning-soft)",
            border: "1px solid var(--ds-color-warning-soft)",
            color: "var(--ds-color-warning)",
          }}
        >
          Your role can view the rubric but cannot save changes.
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT — editor */}
        <div className="space-y-5">
          <TrackPanel
            title="Track A — Startup Funding Path"
            testid="rubric-track-a"
            track={draft.track_a}
            sections={[
              { name: "Credit (max 65 pts)", buckets: CREDIT_BUCKETS, group: "credit_pts" },
              { name: "Payment history (max 25 pts)", buckets: PAYMENT_BUCKETS, group: "payment_history_pts" },
            ]}
            onChange={(group, bucket, value) => setBucket("track_a", group, bucket, value)}
            disabled={!canEdit}
            extra={
              <div className="grid grid-cols-2 gap-3">
                <NumberField testid="rubric-track-a-clear-purpose" label="Clear purpose" value={draft.track_a.funding_preparation_pts.clear_purpose} onChange={(v) => setPrep("clear_purpose", v)} disabled={!canEdit} />
                <NumberField testid="rubric-track-a-goal-amount" label="Goal amount specified" value={draft.track_a.funding_preparation_pts.goal_amount_specified} onChange={(v) => setPrep("goal_amount_specified", v)} disabled={!canEdit} />
              </div>
            }
          />
          <TrackPanel
            title="Track B — Established Business Path"
            testid="rubric-track-b"
            track={draft.track_b}
            sections={[
              { name: "Credit (max 30 pts)", buckets: CREDIT_BUCKETS, group: "credit_pts" },
              { name: "Payment history (max 15 pts)", buckets: PAYMENT_BUCKETS, group: "payment_history_pts" },
              { name: "Revenue (max 30 pts)", buckets: REV_BUCKETS, group: "revenue_pts" },
            ]}
            onChange={(group, bucket, value) => setBucket("track_b", group, bucket, value)}
            disabled={!canEdit}
            extra={
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <NumberField testid="rubric-bf-entity" label="Entity ≠ sole prop" value={draft.track_b.business_foundation_pts.entity_not_sole_prop} onChange={(v) => setBfFlat("entity_not_sole_prop", v)} disabled={!canEdit} />
                  <NumberField testid="rubric-bf-ein" label="Has EIN" value={draft.track_b.business_foundation_pts.has_ein} onChange={(v) => setBfFlat("has_ein", v)} disabled={!canEdit} />
                  <NumberField testid="rubric-bf-bank" label="Business bank" value={draft.track_b.business_foundation_pts.has_business_bank} onChange={(v) => setBfFlat("has_business_bank", v)} disabled={!canEdit} />
                  <NumberField testid="rubric-bf-purpose" label="Clear purpose" value={draft.track_b.business_foundation_pts.clear_purpose} onChange={(v) => setBfFlat("clear_purpose", v)} disabled={!canEdit} />
                </div>
                <div>
                  <GroupLabel>Time in business</GroupLabel>
                  <div className="grid grid-cols-4 gap-2">
                    {TIB_BUCKETS.map((b) => (
                      <NumberField key={b.key} testid={`rubric-bf-tib-${b.key}`} label={b.label} value={draft.track_b.business_foundation_pts.time_in_business[b.key]} onChange={(v) => setBfTib(b.key, v)} disabled={!canEdit} />
                    ))}
                  </div>
                </div>
              </div>
            }
          />
          <Section title="Tier thresholds (score floors)" testid="rubric-tier-thresholds-section">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {TIER_KEYS.map((t) => (
                <NumberField
                  key={t.key}
                  testid={`rubric-tier-${t.key}`}
                  label={t.label}
                  value={draft.tier_thresholds[t.key]}
                  max={100}
                  onChange={(v) => setTier(t.key, v)}
                  disabled={!canEdit}
                />
              ))}
            </div>
            <p className="text-[11px] mt-2" style={{ color: "var(--ds-color-ink-muted)" }}>
              Must be strictly descending: Funding Ready &gt; Almost Ready &gt; Improvement Recommended &gt; Repair Required.
            </p>
          </Section>
        </div>

        {/* RIGHT — what-if */}
        <div className="space-y-4" data-testid="rubric-what-if-column">
          <GroupLabel>Live what-if · 5 scenarios</GroupLabel>
          {scenarioResults.map(({ scenario, saved, draft: draftOut }) => (
            <ScenarioCard key={scenario.id} scenario={scenario} saved={saved} draft={draftOut} dirty={dirty} />
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleSave}
        busy={saving}
        title="Apply rubric changes"
        description="This affects all future readiness calculations. Existing assessments remain on their original rubric until a user requests a new one."
        confirmLabel={saving ? "Saving…" : "Yes, save rubric"}
        testId="rubric-confirm-save"
      />
      <ConfirmDialog
        open={showRestore}
        onCancel={() => setShowRestore(false)}
        onConfirm={handleRestore}
        busy={saving}
        variant="destructive"
        title="Restore default rubric"
        description="This resets every track, sub-category and tier threshold to the values bundled with Informed OS. Your current rubric will be overwritten and a warning audit row recorded."
        confirmLabel={saving ? "Restoring…" : "Yes, restore defaults"}
        testId="rubric-confirm-restore"
      />
    </AdminLayout>
  );
}

/* ------------------------------------------------------------------ */
/* Local helpers                                                        */
/* ------------------------------------------------------------------ */

/**
 * GroupLabel — small uppercase group heading used above bucket grids
 * and the what-if column. Local to ScoringRubric; not lifted.
 */
function GroupLabel({ children }) {
  return (
    <div
      className="text-[10px] uppercase tracking-[0.15em] mb-2"
      style={{ color: "var(--ds-color-ink-muted)" }}
    >
      {children}
    </div>
  );
}

/**
 * Field — uppercase-label + control pair. PROTOTYPE only; kept local
 * per Turn 1 plan. Lifts to `_primitives.jsx` in Turn 2 only if it
 * drops verbatim into CreditCatalog's TextField / NumField / SelectField
 * usage.
 */
function Field({ label, children }) {
  return (
    <label
      className="text-[10px] uppercase tracking-[0.12em] block"
      style={{ color: "var(--ds-color-ink-muted)" }}
    >
      {label}
      {children}
    </label>
  );
}

function TrackPanel({ title, testid, track, sections, onChange, extra, disabled }) {
  return (
    <Section title={title} testid={testid}>
      <div className="space-y-4">
        {sections.map((s) => (
          <div key={s.name}>
            <GroupLabel>{s.name}</GroupLabel>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {s.buckets.map((b) => (
                <NumberField
                  key={`${s.group}-${b.key}`}
                  testid={`rubric-${s.group}-${b.key}`}
                  label={b.label}
                  value={track[s.group]?.[b.key] ?? 0}
                  onChange={(v) => onChange(s.group, b.key, v)}
                  disabled={disabled}
                />
              ))}
            </div>
          </div>
        ))}
        {extra}
      </div>
    </Section>
  );
}

function NumberField({ testid, label, value, max = 100, onChange, disabled }) {
  return (
    <Field label={label}>
      <input
        type="number"
        min={0}
        max={max}
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testid}
        className="mt-1 w-full rounded-[var(--ds-radius-input)] border bg-transparent px-2 py-1.5 text-sm tabular-nums focus:outline-none"
        style={{
          borderColor: "var(--ds-color-input-border)",
          color: "var(--ds-color-ink)",
        }}
        onFocus={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-input-border-strong)")}
        onBlur={(e) => (e.currentTarget.style.borderColor = "var(--ds-color-input-border)")}
      />
    </Field>
  );
}

function Pill({ value, color }) {
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.1em]"
      style={{
        background: `var(--ds-color-${color}-soft)`,
        color: `var(--ds-color-${color})`,
        border: `1px solid var(--ds-color-${color})`,
      }}
    >
      {value || "—"}
    </span>
  );
}

function ScenarioCard({ scenario, saved, draft, dirty }) {
  const changed = dirty && (saved.total_score !== draft.total_score || saved.tier !== draft.tier || saved.funding_path !== draft.funding_path);
  const delta = draft.total_score - saved.total_score;
  return (
    <div
      data-testid={`rubric-scenario-${scenario.id}`}
      className="rounded-[var(--ds-radius-card)] p-4"
      style={{
        background: "var(--ds-color-surface)",
        border: "1px solid var(--ds-color-admin-border)",
      }}
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-semibold" style={{ color: "var(--ds-color-ink)" }}>
            {scenario.id} · {scenario.label}
          </div>
          <div className="text-[11px]" style={{ color: "var(--ds-color-ink-muted)" }}>
            {scenario.summary}
          </div>
        </div>
        {changed && (
          <span
            className="text-[10px] uppercase tracking-[0.15em]"
            style={{ color: "var(--ds-color-accent)" }}
            data-testid={`rubric-scenario-${scenario.id}-delta`}
          >
            {delta > 0 ? `+${delta}` : delta} pts
          </span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <ResultColumn label="Saved" out={saved} />
        <ResultColumn label="With edits" out={draft} highlight={changed} />
      </div>
    </div>
  );
}

function ResultColumn({ label, out, highlight }) {
  return (
    <div
      className="rounded-[var(--ds-radius-input)] p-3"
      style={{
        background: highlight ? "var(--ds-color-accent-soft)" : "transparent",
        border: highlight
          ? "1px solid var(--ds-color-accent)"
          : "1px solid var(--ds-color-admin-border)",
      }}
    >
      <div className="flex items-center justify-between mb-1">
        <span
          className="text-[10px] uppercase tracking-[0.15em]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          {label}
        </span>
        <span
          className="font-display text-[24px] tabular-nums"
          style={{ color: "var(--ds-color-ink)" }}
        >
          {out.total_score}
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Pill value={out.tier?.replace(/_/g, " ")} color={TIER_COLOR[out.tier]} />
        <ArrowRight className="w-3 h-3" style={{ color: "var(--ds-color-ink-muted)" }} />
        <Pill value={out.funding_path} color={PATH_COLOR[out.funding_path]} />
      </div>
      <div
        className="grid grid-cols-2 gap-x-3 mt-2 text-[10px] tabular-nums"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        <span>Track A: <span>{out.track_a_score}</span></span>
        <span>Track B: <span>{out.track_b_applicable ? out.track_b_score : "—"}</span></span>
      </div>
    </div>
  );
}
